package com.arena_web.utils

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import org.w3c.dom.HTMLAnchorElement
import kotlinx.coroutines.async
import kotlinx.coroutines.await
import kotlinx.coroutines.coroutineScope
import org.w3c.fetch.Response
import kotlin.js.Date
import kotlin.js.Promise

data class GeoCoordinates(val latitude: Double, val longitude: Double)

/**
 * Open the location in a maps app/site. Searches by the human-readable address
 * so it shows up in the maps search bar; falls back to raw coordinates when no
 * address is available. Uses a synthesized `<a target="_blank">` click instead
 * of `window.open`, which some browsers (notably iOS) leave as a blank tab.
 * No-op when nothing usable is provided.
 */
fun openInMaps(latitude: String?, longitude: String?, address: String?) {
    val addr = address?.trim().orEmpty()
    val lat = latitude?.trim().orEmpty()
    val lng = longitude?.trim().orEmpty()
    val query = if (addr.isNotEmpty()) addr
        else if (lat.isNotEmpty() && lng.isNotEmpty()) "$lat,$lng"
        else ""
    if (query.isEmpty()) return
    val encoded = js("encodeURIComponent")(query) as String
    val url = "https://www.google.com/maps?q=$encoded"
    val a = document.createElement("a") as HTMLAnchorElement
    a.href = url
    a.target = "_blank"
    a.rel = "noopener noreferrer"
    document.body?.appendChild(a)
    a.click()
    document.body?.removeChild(a)
}

data class AddressResult(
    val displayName: String,
    val lat: Double,
    val lng: Double,
    val city: String?,
    val street: String? = null,
    val houseNumber: String? = null,
    val country: String? = null
) {
    /** e.g. "вул. Хрещатик, 22" or "Khreshchatyk Street, 22" */
    val title: String
        get() {
            val parts = listOfNotNull(street, houseNumber).filter { it.isNotBlank() }
            return if (parts.isNotEmpty()) parts.joinToString(", ") else displayName.split(",").first().trim()
        }

    /** e.g. "Kyiv, Ukraine" */
    val subtitle: String
        get() = listOfNotNull(city, country).filter { it.isNotBlank() }.joinToString(", ")
}

/**
 * Coarse user location resolved from the browser's public IP (via ipapi.co —
 * free, no API key, ~1000 req/day). Cached for the session so we don't refetch
 * on every keystroke. Used to bias autocomplete suggestions toward cities near
 * the user without prompting for GPS permission.
 */
data class IpLocation(
    val lat: Double,
    val lng: Double,
    val country: String? = null,
    /** ISO 3166-1 alpha-2 code in lowercase ("pl", "ua", "us") — Nominatim's
     *  `countrycodes` param needs this form to filter results by country. */
    val countryCode: String? = null,
)

private var cachedIpLocation: IpLocation? = null
private var lastIpFailureMs = 0.0

// When every IP-geo provider fails, retry after a minute instead of caching
// the miss for the session — a transient failure must not disable IP-biasing
// until the page reloads, but per-keystroke searches must not hammer dead
// endpoints either.
private const val IP_RETRY_COOLDOWN_MS = 60_000.0

/**
 * Cached for the session once resolved — the public IP doesn't move
 * mid-session. Providers are tried in order: ipapi.co rate-limits free
 * traffic aggressively (HTTP 429), so geojs.io and ipwho.is back it up.
 */
suspend fun getIpLocation(): IpLocation? {
    cachedIpLocation?.let { return it }
    if (Date.now() - lastIpFailureMs < IP_RETRY_COOLDOWN_MS) return null
    val result = fetchIpapiCo() ?: fetchGeoJs() ?: fetchIpWhoIs()
    if (result != null) cachedIpLocation = result else lastIpFailureMs = Date.now()
    return result
}

private suspend fun fetchIpapiCo(): IpLocation? = ipProvider("ipapi.co") {
    val response: Response = window.fetch("https://ipapi.co/json/").await()
    if (!response.ok) return@ipProvider null
    val json: dynamic = JSON.parse(response.text().await())
    val lat = (json.latitude as? Number)?.toDouble() ?: return@ipProvider null
    val lng = (json.longitude as? Number)?.toDouble() ?: return@ipProvider null
    IpLocation(lat, lng, json.country_name as? String, (json.country_code as? String)?.lowercase())
}

private suspend fun fetchGeoJs(): IpLocation? = ipProvider("geojs.io") {
    val response: Response = window.fetch("https://get.geojs.io/v1/ip/geo.json").await()
    if (!response.ok) return@ipProvider null
    val json: dynamic = JSON.parse(response.text().await())
    // geojs serialises coordinates as strings.
    val lat = (json.latitude as? String)?.toDoubleOrNull() ?: return@ipProvider null
    val lng = (json.longitude as? String)?.toDoubleOrNull() ?: return@ipProvider null
    IpLocation(lat, lng, json.country as? String, (json.country_code as? String)?.lowercase())
}

private suspend fun fetchIpWhoIs(): IpLocation? = ipProvider("ipwho.is") {
    val response: Response = window.fetch("https://ipwho.is/").await()
    if (!response.ok) return@ipProvider null
    val json: dynamic = JSON.parse(response.text().await())
    // ipwho.is reports failures as 200 + success=false.
    if (json.success == false) return@ipProvider null
    val lat = (json.latitude as? Number)?.toDouble() ?: return@ipProvider null
    val lng = (json.longitude as? Number)?.toDouble() ?: return@ipProvider null
    IpLocation(lat, lng, json.country as? String, (json.country_code as? String)?.lowercase())
}

private suspend fun ipProvider(name: String, block: suspend () -> IpLocation?): IpLocation? =
    try {
        block()
    } catch (e: CancellationException) {
        // Debounced searches relaunch on every keystroke; the superseded
        // LaunchedEffect is cancelled with "The coroutine scope left the
        // composition". Propagate it so we don't log it as an error or poison
        // the failure cooldown with a cancellation that isn't a real miss.
        throw e
    } catch (e: Exception) {
        console.log("IP location error ($name): ${e.message}")
        null
    }

private var cachedUserCoords: GeoCoordinates? = null

/** Cache-only accessor — per-keystroke callers (address autocomplete bias)
 *  must never await a GPS fix. Populated by [prefetchUserGeo] at app start
 *  and upgraded by any later [getUserCoordinates] success. */
fun cachedUserCoordinates(): GeoCoordinates? = cachedUserCoords

/**
 * Resolve the user's coarse location ONCE at page load and cache it for the
 * session. GPS is only used when the permission is ALREADY granted — the
 * browser prompt belongs to user-initiated flows (create-event popup), not a
 * page load. Otherwise coordinates come from the IP lookup.
 */
suspend fun prefetchUserGeo() {
    if (cachedUserCoords != null) return
    val granted = try {
        val perms: dynamic = window.navigator.asDynamic().permissions
        if (perms != null && perms != undefined) {
            val status: dynamic = (perms.query(js("({name: 'geolocation'})")) as Promise<dynamic>).await()
            (status.state as? String) == "granted"
        } else {
            false
        }
    } catch (e: CancellationException) {
        throw e
    } catch (e: Exception) {
        false
    }
    if (granted && getUserCoordinates() != null) return
    getIpLocation()?.let { cachedUserCoords = GeoCoordinates(it.lat, it.lng) }
}

suspend fun getUserCoordinates(): GeoCoordinates? {
    return try {
        val coords = Promise<GeoCoordinates> { resolve, reject ->
            val nav: dynamic = window.navigator.asDynamic()
            val geo: dynamic = nav.geolocation
            if (geo == null || geo == undefined) {
                reject(Exception("Geolocation not supported"))
                return@Promise
            }
            geo.getCurrentPosition(
                { pos: dynamic ->
                    val lat: Double = pos.coords.latitude as Double
                    val lng: Double = pos.coords.longitude as Double
                    console.log("Geolocation success: $lat, $lng")
                    resolve(GeoCoordinates(latitude = lat, longitude = lng))
                },
                { error: dynamic ->
                    console.log("Geolocation error callback:", error.message)
                    reject(Exception(error.message as? String ?: "Geolocation denied"))
                },
                js("({enableHighAccuracy: false, timeout: 10000, maximumAge: 0})")
            )
        }.await()
        // GPS beats an IP-derived cache entry; a denied/failed fix keeps it.
        cachedUserCoords = coords
        coords
    } catch (e: Exception) {
        console.log("Geolocation error: ${e.message}")
        cachedUserCoords
    }
}

suspend fun searchAddress(
    query: String,
    nearLat: Double? = null,
    nearLng: Double? = null,
    // ISO 3166-1 alpha-2 (lowercase). When set, restricts results to that
    // country so a strong text match abroad can't outrank local addresses.
    countryCode: String? = null
): List<AddressResult> {
    return try {
        val encoded = js("encodeURIComponent")(query) as String
        val viewbox = if (nearLat != null && nearLng != null) {
            val delta = 0.5
            "&viewbox=${nearLng - delta},${nearLat + delta},${nearLng + delta},${nearLat - delta}&bounded=0"
        } else ""
        val countryParam = if (!countryCode.isNullOrBlank()) "&countrycodes=$countryCode" else ""
        // accept-language=en forces Nominatim to return city/road/country
        // labels in English regardless of the user's browser locale or the
        // local language at the coordinates. Without it, geo lookups in e.g.
        // Ukraine come back Cyrillic and never match events stored with the
        // English spelling.
        val url = "https://nominatim.openstreetmap.org/search?format=json&q=$encoded&addressdetails=1&limit=8&accept-language=en$viewbox$countryParam"
        val response: Response = window.fetch(
            url,
            js("({headers: {'User-Agent': 'ArenaWebApp/1.0'}})")
        ).await()
        if (!response.ok) return emptyList()
        val text = response.text().await()
        val results: dynamic = JSON.parse(text)
        val list = mutableListOf<AddressResult>()
        val length = (results.length as? Int) ?: 0
        for (i in 0 until length) {
            val item: dynamic = results[i]
            val displayName = item.display_name as? String ?: continue
            val lat = (item.lat as? String)?.toDoubleOrNull() ?: continue
            val lng = (item.lon as? String)?.toDoubleOrNull() ?: continue
            val addr: dynamic = item.address
            val city = addr?.city ?: addr?.town ?: addr?.village ?: addr?.municipality
            val street = addr?.road as? String
            val houseNumber = addr?.house_number as? String
            val country = addr?.country as? String
            list.add(AddressResult(displayName, lat, lng, city as? String, street, houseNumber, country))
        }
        list
    } catch (e: CancellationException) {
        throw e
    } catch (e: Exception) {
        console.log("Address search error: ${e.message}")
        emptyList()
    }
}

/** Plain city suggestion (no street/house data) — used by city-only autocomplete. */
data class CityResult(
    val city: String,
    val country: String?,
    val lat: Double,
    val lng: Double,
) {
    val display: String get() = listOfNotNull(city, country).filter { it.isNotBlank() }.joinToString(", ")
}

/**
 * Search Nominatim for cities only — filters out streets, POIs, addresses etc.
 * Nominatim returns results with `class` and `type` fields; we keep only
 * `class:boundary/place` with `type:city/town/village/municipality/...`.
 */
suspend fun searchCities(query: String): List<CityResult> {
    val encoded = js("encodeURIComponent")(query) as String
    val baseUrl = "https://nominatim.openstreetmap.org/search?format=json&q=$encoded" +
        "&addressdetails=1&limit=10&featuretype=city&accept-language=en"

    // Two-pass search: one filtered to the user's country (strong local
    // preference — `viewbox` alone is only a weak Nominatim hint and barely
    // affects ranking for short queries like "Wa"), one global. Local hits
    // bubble to the top, global fills the remainder. Both fired in parallel
    // so total latency matches a single Nominatim round-trip.
    val loc = getIpLocation()
    val cc = loc?.countryCode
    val viewbox = if (loc != null) {
        val delta = 3.0
        "&viewbox=${loc.lng - delta},${loc.lat + delta},${loc.lng + delta},${loc.lat - delta}&bounded=0"
    } else ""
    val localUrl = if (cc != null) "$baseUrl&countrycodes=$cc" else null
    val globalUrl = "$baseUrl$viewbox"

    val (localResults, globalResults) = coroutineScope {
        val local = async { if (localUrl != null) fetchCities(localUrl) else emptyList() }
        val global = async { fetchCities(globalUrl) }
        local.await() to global.await()
    }

    // Local cities first, then global — dedupe by city+country.
    val seen = mutableSetOf<String>()
    val merged = mutableListOf<CityResult>()
    (localResults + globalResults).forEach { c ->
        val key = "${c.city}|${c.country.orEmpty()}".lowercase()
        if (seen.add(key)) merged.add(c)
    }
    return merged.take(10)
}

private suspend fun fetchCities(url: String): List<CityResult> {
    return try {
        val response: Response = window.fetch(
            url,
            js("({headers: {'User-Agent': 'ArenaWebApp/1.0'}})")
        ).await()
        if (!response.ok) return emptyList()
        val text = response.text().await()
        val results: dynamic = JSON.parse(text)
        val list = mutableListOf<CityResult>()
        val length = (results.length as? Int) ?: 0
        val cityTypes = setOf("city", "town", "village", "municipality", "hamlet", "borough", "administrative")
        val seen = mutableSetOf<String>()
        for (i in 0 until length) {
            val item: dynamic = results[i]
            val type = item.type as? String
            if (type !in cityTypes) continue
            val lat = (item.lat as? String)?.toDoubleOrNull() ?: continue
            val lng = (item.lon as? String)?.toDoubleOrNull() ?: continue
            val addr: dynamic = item.address
            val cityName = (addr?.city ?: addr?.town ?: addr?.village ?: addr?.municipality ?: item.name) as? String
                ?: continue
            val country = addr?.country as? String
            val key = "${cityName}|${country.orEmpty()}".lowercase()
            if (!seen.add(key)) continue
            list.add(CityResult(cityName, country, lat, lng))
        }
        list
    } catch (e: CancellationException) {
        throw e
    } catch (e: Exception) {
        console.log("City search error: ${e.message}")
        emptyList()
    }
}

suspend fun reverseGeocodeCity(lat: Double, lng: Double): String? {
    return try {
        val url = "https://nominatim.openstreetmap.org/reverse?format=json&lat=$lat&lon=$lng&zoom=10&addressdetails=1&accept-language=en"
        val response: Response = window.fetch(
            url,
            js("({headers: {'User-Agent': 'ArenaWebApp/1.0'}})")
        ).await()
        if (!response.ok) return null
        val text = response.text().await()
        val json = JSON.parse<dynamic>(text)
        val address: dynamic = json.address
        val city = address?.city ?: address?.town ?: address?.village ?: address?.municipality
        (city as? String)
    } catch (e: CancellationException) {
        throw e
    } catch (e: Exception) {
        console.log("Reverse geocode error: ${e.message}")
        null
    }
}

suspend fun reverseGeocode(lat: Double, lng: Double): AddressResult? {
    return try {
        val url = "https://nominatim.openstreetmap.org/reverse?format=json&lat=$lat&lon=$lng&zoom=18&addressdetails=1&accept-language=en"
        val response: Response = window.fetch(
            url,
            js("({headers: {'User-Agent': 'ArenaWebApp/1.0'}})")
        ).await()
        if (!response.ok) return null
        val text = response.text().await()
        val json = JSON.parse<dynamic>(text)
        val displayName = json.display_name as? String ?: return null
        val addr: dynamic = json.address
        val city = addr?.city ?: addr?.town ?: addr?.village ?: addr?.municipality
        // Pull street + house number (and country) so the title reads as a real
        // address ("Khreshchatyk Street, 22") instead of just the building number.
        val street = (addr?.road ?: addr?.pedestrian ?: addr?.footway) as? String
        val houseNumber = addr?.house_number as? String
        val country = addr?.country as? String
        AddressResult(displayName, lat, lng, city as? String, street, houseNumber, country)
    } catch (e: CancellationException) {
        throw e
    } catch (e: Exception) {
        console.log("Reverse geocode error: ${e.message}")
        null
    }
}
